import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, get, isNumber, omit } from 'lodash-es';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { EventToolbox } from 'client/utils/event-toolbox';
import { ErrorState } from 'site-modules/shared/components/lead-form/error-state/error-state';
import { logger } from 'client/utils/logger/logger';
import { EdmundsAPI } from 'client/data/api/api-client';
import { makePromiseCancelable } from 'client/utils/promise';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { insiderActions } from 'site-modules/shared/components/lead-form/utils/insider';
import { flavorStore } from 'client/utils/flavor';
import { FLAVOR_GROUP_NAMES } from 'client/site-modules/shared/constants/flavors';

import { getLeadIdListByPositions } from 'client/engagement-handlers/lead-form-engagement-handler/lead-form-engagement-helper';
import { LEAD_TYPES } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { LEAD_FORM_STATES } from 'site-modules/shared/constants/lead-form/lead-form-states';
import { LEAD_FORM_EVENTS } from 'site-modules/shared/constants/lead-form/lead-form-events';
import { buildLeadTargetsApiUrl } from 'client/site-modules/shared/utils/lead-form/build-lead-targets-api-url';
import { UnifiedLeadFormCustomConfigPropTypes } from 'site-modules/shared/components/lead-form/lead-form-prop-types/lead-form-prop-types';
import {
  getLeadUid,
  getLeadDealerId,
  getRooftopId,
  getLeadToSubmit,
  getPrimaryStyleId,
  getCarMaxComment,
  getVehicleStatus,
} from 'site-modules/shared/components/lead-form/utils/utils';
import { LeadFormValidationWrapper } from 'site-modules/shared/components/lead-form/lead-form-validation-wrapper/lead-form-validation-wrapper';
import { getMetricsHeaders } from 'client/utils/metrics-header';
import { TrackingConstant } from 'client/tracking/constant';
import { LEADDELIVERY_API_URL } from 'client/site-modules/shared/constants/lead-form/lead-form-configs';
import { isPickupType } from 'site-modules/shared/utils/is-pickup-type';
import {
  adjustLeadSubmissionURL,
  getCurrentCreativeId,
  getFeatureFlags,
  getTags,
} from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-utils';
import './lead-form.scss';

/**
 * Wraps lead form and gives access to props and methods.
 */
export class LeadFormWrapperUI extends Component {
  static propTypes = {
    leadTargets: PropTypes.arrayOf(PropTypes.shape({})),
    className: PropTypes.string,
    loadingComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    leadFormComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    thankYouComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    emptyLeadFormComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    config: PropTypes.shape({
      strategy: PropTypes.string.isRequired,
      leadType: PropTypes.string.isRequired,
      submitLeadsType: PropTypes.string.isRequired,
      dataTrackingParent: PropTypes.shape({
        creativeId: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    customConfig: PropTypes.shape(UnifiedLeadFormCustomConfigPropTypes).isRequired,
    validationConfig: PropTypes.shape({}),
    leadFormData: PropTypes.shape({}),
    visitorId: PropTypes.string,
    sessionId: PropTypes.string,
    venomVersion: PropTypes.string,
    pageName: PropTypes.string,
    vin: PropTypes.string,
    zipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    stateCode: PropTypes.string,
    dma: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    make: PropTypes.string,
    model: PropTypes.string,
    submodel: PropTypes.string,
    submodelIdentifier: PropTypes.string,
    dealerName: PropTypes.string,
    styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trim: PropTypes.string,
    specialIdentifier: PropTypes.string,
    bodyType: PropTypes.string,
    pathname: PropTypes.string,
    profile: PropTypes.shape({}).isRequired,
    createOrUpdateIdmData: PropTypes.func.isRequired,
    closeDrawer: PropTypes.func,
    updateStyle: PropTypes.func,
  };

  static defaultProps = {
    leadTargets: [],
    loadingComponent: null,
    emptyLeadFormComponent: null,
    className: '',
    visitorId: '',
    sessionId: '',
    venomVersion: '',
    pageName: '',
    radius: '50',
    vin: '',
    year: '',
    make: '',
    model: '',
    submodel: '',
    submodelIdentifier: '',
    dealerName: '',
    styleId: '',
    trim: '',
    specialIdentifier: '',
    bodyType: '',
    stateCode: '',
    dma: '',
    latitude: null,
    longitude: null,
    pathname: '',
    leadFormData: {},
    validationConfig: {},
    closeDrawer: null,
    updateStyle: null,
  };

  constructor(props) {
    super(props);
    this.leadFormContainer = React.createRef();

    this.selectedLeadTargets = [];
    this.leadIds = [];
    this.customThankYouData = {};
    this.submittedTrackingData = {};
    // this style id can be set by user (QQ) or set from page (Core page)
    // if style id is not set, it means dealer is suggested, style id will be obtained from primary lead inventory
    // if primary lead inventory does not exist then will be obtained from current lead target on submission
    this.primaryStyleId = 0;

    this.state = {
      shouldUpdateLeadTargets: true,
      submodel: props.submodel,
      submodelIdentifier: props.submodelIdentifier,
      styleId: props.styleId,
      trim: props.trim,
      prevPropsStyleId: props.styleId,
      leadTargets: get(props, 'customConfig.useLeadTargetsFromProps') ? props.leadTargets : [],
      selectedLeadIds: {},
      formState: get(props, 'customConfig.renderFormServerSide')
        ? LEAD_FORM_STATES.LEAD_FORM
        : LEAD_FORM_STATES.LOADING,
      leadFormStrategy: get(props, 'config.strategy'),
      showListTargetsLoadingIndicator: false,
      showSubmitLoadingIndicator: false,
      isCheckboxProcessed: false,
      isCheckboxProcessError: false,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    // core pricing lead form states, styleId was updated by props
    if (nextProps.styleId !== prevState.prevPropsStyleId && !prevState.shouldUpdateLeadTargets) {
      return {
        styleId: nextProps.styleId,
        prevPropsStyleId: nextProps.styleId,
        shouldUpdateLeadTargets: true,
        formState: LEAD_FORM_STATES.LOADING,
      };
    }
    // quick quotes lead from states
    if (
      nextProps.zipCode !== prevState.zipCode ||
      prevState.shouldUpdateLeadTargets ||
      (get(nextProps, 'customConfig.useLeadTargetsFromProps') && !isEqual(nextProps.leadTargets, prevState.leadTargets))
    ) {
      return { shouldUpdateLeadTargets: true };
    }
    return null;
  }

  componentDidMount() {
    const { customConfig } = this.props;
    const { leadType, isUserOpened, leadFormConfig } = customConfig;

    EventToolbox.fireCustomEvent(
      LEAD_FORM_EVENTS.DISPLAY,
      { leadType, isUserOpened, leadFormConfig },
      this.leadFormContainer.current
    );
    this.getLeadTargets();
  }

  componentDidUpdate() {
    this.getLeadTargets();
  }

  componentWillUnmount() {
    if (this.promiseCancelable) {
      this.promiseCancelable.cancel();
    }
  }

  /**
   * Gets url for lead targets.
   * @returns {string}
   */
  getLeadTargetsApiUrl = () => {
    const {
      vin,
      zipCode,
      latitude,
      longitude,
      dma,
      make,
      model,
      year,
      bodyType,
      radius,
      stateCode,
      customConfig: {
        apiParams,
        leadFormConfig: { showLeasePayments },
      },
    } = this.props;
    const { submodel, submodelIdentifier, styleId, trim, leadFormStrategy } = this.state;
    // some cars sometime don't have sub-model identifier
    const apiSubModel = submodelIdentifier || makeNiceName(submodel);
    const encodedTrim = encodeURIComponent(trim);
    const { leadTargetsSize, omitStyleId, omitYear } = apiParams;
    const withLeasePayment = showLeasePayments
      ? ',estimateLeasePromise.monthlyPayment,estimateLeasePromise.downPayment,estimateLeasePromise.term'
      : '';

    return buildLeadTargetsApiUrl({
      make,
      model,
      year,
      styleId,
      omitStyleId,
      omitYear,
      strategy: leadFormStrategy,
      zipCode,
      latitude,
      longitude,
      dma,
      radius,
      stateCode,
      vin,
      leadTargetsSize,
      isPickup: isPickupType(bodyType),
      apiSubModel,
      encodedTrim,
      withLeasePayment,
      targetGroups: flavorStore.findByGroup(FLAVOR_GROUP_NAMES.TARGET_GROUP),
    });
  };

  /**
   * Gets lead targets and call processing.
   * @returns {Promise}
   */
  getLeadTargets = () => {
    if (!this.state.shouldUpdateLeadTargets) {
      return Promise.resolve(true);
    }
    this.setState({
      showListTargetsLoadingIndicator: this.props.zipCode !== this.state.zipCode, // should be only visible when zipCode was changed
      shouldUpdateLeadTargets: false,
      zipCode: this.props.zipCode,
    });

    if (get(this.props, 'customConfig.noLeadTargets')) {
      return this.setState({
        leadTargets: [],
        showListTargetsLoadingIndicator: false,
        formState: LEAD_FORM_STATES.LEAD_FORM,
      });
    }

    if (get(this.props, 'customConfig.useLeadTargetsFromProps')) {
      this.processLeadTargetsResponse(this.props.leadTargets);
      return Promise.resolve(true);
    }

    const { pageName, venomVersion } = this.props;

    const url = this.getLeadTargetsApiUrl();

    this.promiseCancelable = makePromiseCancelable(
      EdmundsAPI.fetchJson(url, { headers: getMetricsHeaders('target', pageName, venomVersion) })
    );
    return this.promiseCancelable.promise
      .then(json => this.processLeadTargetsResponse(json.results))
      .catch(error => {
        this.fireListEvent([]);
        if (error.isCanceled) return;
        this.setState({
          leadTargets: [],
          showListTargetsLoadingIndicator: false,
          formState: LEAD_FORM_STATES.LOAD_DATA_ERROR,
        });
      });
  };

  /**
   * Gets selected leads info and transform for lead submission.
   * @param {string} [comments='']
   * @returns {object[]}
   */
  getSelectedLeadSubmissionInfo = (comments = '') => {
    const { leadTargets, styleId } = this.state;
    const {
      config: { leadType },
    } = this.props;

    const positions = {};

    // get selected leads full info from lead targets by uid
    const selectedLeadTargets = this.getSelectedLeadTargets();

    // get leads position
    leadTargets.forEach((leadTarget, position) => {
      positions[getLeadUid(leadTarget)] = position;
    });

    // this style id can be set by user (QQ) or set from page (Core page)
    // if style id is not set, it means dealer is suggested, style id will be obtained from primary lead inventory
    this.primaryStyleId = this.primaryStyleId || styleId || getPrimaryStyleId(leadTargets);

    // return lead targets array with necessary fields for submit
    return selectedLeadTargets.map(leadTarget =>
      this.isLeadDeliveryURL()
        ? {
            rooftopId: getRooftopId(leadTarget),
            position: positions[getLeadUid(leadTarget)],
            vehicleStatus: getVehicleStatus(leadTarget),
            leadType,
            comments: comments + getCarMaxComment(get(leadTarget, 'inventory', {})),
            ...getLeadToSubmit(leadTarget, this.primaryStyleId),
          }
        : {
            dealerId: getLeadDealerId(leadTarget),
            position: positions[getLeadUid(leadTarget)],
            vehicleStatus: getVehicleStatus(leadTarget),
            leadType,
            comments: comments + getCarMaxComment(get(leadTarget, 'inventory', {})),
            ...getLeadToSubmit(leadTarget, this.primaryStyleId),
          }
    );
  };

  /**
   * Gets full lead info for submit.
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} phone
   * @param {string} email
   * @param {boolean} isPrepaidPhone
   * @param {object} leads
   * @param {any} additionalData
   * @returns {object}
   */
  getLeadSubmissionInfo = ({ firstName, lastName, phone, email, isPrepaidPhone }, leads, additionalData) => {
    const { visitorId, sessionId, zipCode, pathname, customConfig, config } = this.props;
    const { tags: tagsFromProps } = customConfig;
    const { leadTargets } = this.state;

    const creativeId = getCurrentCreativeId(config, customConfig);
    const featureFlags = getFeatureFlags(customConfig);
    const tags = getTags(isPrepaidPhone, tagsFromProps);

    const {
      processDataMethods: { getLeadSubmissionInfo },
    } = customConfig;

    // default function placed in `client/site-modules/shared/components/lead-form/utils/utils.js`
    return getLeadSubmissionInfo({
      leads,
      creativeId,
      pathname,
      leadTargets,
      visitorId,
      sessionId,
      tags,
      featureFlags,
      zipCode,
      phone,
      email,
      firstName,
      lastName,
      ...additionalData,
    });
  };

  /**
   * Return tracking data for thank-you page
   * @param submissionLeads
   * @param [] leadIds [value1, value2]
   * @returns {Object} { [null, value1, value2, null], [null, value1, value2, null] }
   */
  getSubmittedTrackingData = (submissionLeads, leadIds) => {
    const positions = submissionLeads.map(lead => lead.position);
    const { leadTargets } = this.state;

    const leadIdList = getLeadIdListByPositions(leadTargets, positions, leadIds);
    return { leadIdList };
  };

  /**
   * Return selected lead targets
   * @returns {*[]}
   */
  getSelectedLeadTargets = () => {
    const { leadTargets, selectedLeadIds } = this.state;
    return leadTargets.filter(leadTarget => selectedLeadIds[getLeadUid(leadTarget)]);
  };

  fireListEvent = leadTargets => {
    const { radius } = this.props;

    EventToolbox.fireCustomEvent(
      LEAD_FORM_EVENTS.LIST,
      {
        leadTargets: [...leadTargets],
        trackingData: {
          subactionName: TrackingConstant.DISPLAY_LEAD_FORM,
          radius,
        },
      },
      this.leadFormContainer.current
    );
  };

  fireSubmit = () => {
    const {
      config: { submitLeadsType },
      customConfig: {
        leadFormConfig: { isSimulatedLeadForm },
      },
    } = this.props;

    EventToolbox.fireCustomEvent(
      LEAD_FORM_EVENTS.SUBMIT,
      {
        total: Object.keys(this.state.selectedLeadIds).length,
        isBlended: 'n',
        type: submitLeadsType,
        isSimulatedLeadForm,
      },
      this.leadFormContainer.current
    );
  };

  /**
   * Process received lead targets.
   * @param leadTargetsResult {object} json
   */
  processLeadTargetsResponse = leadTargetsResult => {
    const {
      customConfig: { leadType, showDealerMakeStockImage, leadFormConfig },
    } = this.props;

    const results = leadTargetsResult || [];
    let leadTargets = leadType === LEAD_TYPES.ANY ? results : results.filter(leadTarget => leadTarget[leadType]);
    let formState =
      leadTargets.length || leadType === LEAD_TYPES.ANY || leadFormConfig.showLeadFormOnEmptyTargets
        ? LEAD_FORM_STATES.LEAD_FORM
        : LEAD_FORM_STATES.LOAD_DATA_ERROR;

    if (showDealerMakeStockImage) {
      // we need info about vehicle, try get from props
      const { year, make, model } = this.props;
      const vehicleInfo = { year, make, model };
      leadTargets = leadTargets.map(leadTarget => {
        if (leadTarget.dealer) {
          const dealerLead = {
            ...leadTarget,
            dealer: {
              ...leadTarget.dealer,
              vehicleInfo,
            },
          };
          return dealerLead;
        }
        return leadTarget;
      });
    }
    const selectedLeadIds = this.processSelectedLeadsFromResponse(leadTargets);

    // hide lead form in cases when:
    // 1. lead form state is lead form and lead targets are empty, hideWhenLeadTargetsEmpty is true.
    if (formState === LEAD_FORM_STATES.LEAD_FORM && leadFormConfig.hideWhenLeadTargetsEmpty && isEmpty(leadTargets)) {
      formState = LEAD_FORM_STATES.HIDDEN;
    }

    // it need to prevent thank-you-lead-form event in the wrong data-tracking-parent = lead_form
    // event will fire in in ThankYouViewDecorator
    // TODO: this is quick solution, need review in the future
    if (formState === LEAD_FORM_STATES.LEAD_FORM || formState === LEAD_FORM_STATES.LOAD_DATA_ERROR) {
      this.fireListEvent(leadTargets);
    }

    this.setState({
      shouldUpdateLeadTargets: false,
      showListTargetsLoadingIndicator: false,
      showSubmitLoadingIndicator: false,
      leadTargets,
      selectedLeadIds,
      formState,
    });
  };

  processSelectedLeadsFromResponse = leadTargets => {
    const preSelectedCardNumbers = get(this.props.customConfig, 'leadFormConfig.preSelectedCardNumbers', null);

    const selectedLeadIds = {};
    leadTargets.forEach((item, index) => {
      const uid = getLeadUid(item);
      if (isNumber(preSelectedCardNumbers)) {
        if (index < preSelectedCardNumbers) {
          selectedLeadIds[uid] = true;
        }
      } else if (item.leadAttributes.selected) {
        selectedLeadIds[uid] = true;
      }
    });
    return selectedLeadIds;
  };

  /**
   * Submits selected inventory and user personal information (name, phone, email)
   * thank you page, otherwise after submit shows thank you page w/o request for thank you leads.
   * If an error occurred on API call error message will be displayed.
   * @param {object} personalInfo
   * @param {string} personalInfo.firstName
   * @param {string} personalInfo.lastName
   * @param {string} personalInfo.phone
   * @param {string} personalInfo.email
   * @param {string} personalInfo.phoneType
   * @param {string} personalInfo.comments
   * @param {boolean} personalInfo.isPrepaidPhone
   * @param {string} personalInfo.validationInfoTag
   * @param {boolean} personalInfo.isCheckboxChecked
   * @param {any} additionalData
   * @returns {Promise|Boolean}
   */
  submitLeadForm = (personalInfo, additionalData) => {
    const {
      customConfig: {
        apiParams: { leadSubmissionAPI, leadSubmissionURL },
        processDataMethods: { getLeadSubmissionAPIHeaders, processCheckedCheckbox, postProcessMethods },
        leadFormConfig: { isSimulatedLeadForm },
      },
      pageName,
      venomVersion,
    } = this.props;

    const { comments } = personalInfo;

    const leads = this.getSelectedLeadSubmissionInfo(comments);
    const submissionInfo = this.getLeadSubmissionInfo(personalInfo, leads, additionalData);
    const adjustedLeadSubmissionURL = adjustLeadSubmissionURL(additionalData, leadSubmissionURL);

    const isLeadDeliveryURL = this.isLeadDeliveryURL();
    if (isLeadDeliveryURL) {
      submissionInfo.vehicleInquiries = submissionInfo.leads;
    }

    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getLeadSubmissionAPIHeaders('multi', pageName, venomVersion),
      },
      body: JSON.stringify(
        isLeadDeliveryURL
          ? omit(submissionInfo, ['leads', 'submissionInfo.featureFlags', 'submissionInfo.skipEmail'])
          : submissionInfo
      ),
      retries: 0,
    };

    this.selectedLeadTargets = this.getSelectedLeadTargets();
    this.personalInfo = personalInfo;

    this.setState({
      showSubmitLoadingIndicator: true,
    });

    // TODO: remove when clean up TED-7440-Basic-Listing-Lead-Vaporware
    if (isSimulatedLeadForm) {
      this.fireSubmit();
      this.showThankYouPage();
      return Promise.resolve(true);
    }

    this.promiseCancelable = makePromiseCancelable(leadSubmissionAPI.fetchJson(adjustedLeadSubmissionURL, params));

    return this.promiseCancelable.promise
      .then(res => {
        this.additionalData = additionalData;
        this.leadIds = res.leadIds;
        this.submittedTrackingData = this.getSubmittedTrackingData(leads, this.leadIds);

        this.fireSubmit();

        if (this.personalInfo.isCheckboxChecked) {
          processCheckedCheckbox({
            selectedLeadTargets: this.selectedLeadTargets,
            leadTargets: this.state.leadTargets,
            personalInfo: this.personalInfo,
            additionalData: this.additionalData,
          })
            .then(() => {
              this.setState({
                isCheckboxProcessed: true,
              });
            })
            .catch(() => {
              this.setState({
                isCheckboxProcessError: true,
              });
            });
        }

        return this.processLeadTargetsAfterSubmit().then(() =>
          Promise.all(postProcessMethods.map(postProcess => postProcess.bind(this)(additionalData)))
        );
      })
      .catch(err => {
        if (err.isCanceled) {
          return;
        }
        this.submitLeadFormError(err);
      });
  };

  // Run some Edmunds Insider things
  runInsiderAction = async () => {
    const {
      zipCode,
      createOrUpdateIdmData,
      profile,
      modelLinkCode, // it comes from insiderMethods
      customConfig: {
        processDataMethods: { getInsiderSaveData },
      },
    } = this.props;

    const additionalInsiderData = getInsiderSaveData({
      zipCode,
      personalInfo: this.personalInfo,
      leadIds: this.leadIds,
      selectedLeadTargets: this.selectedLeadTargets,
      ...this.additionalData,
    });

    // Run some Edmunds Insider things
    // should always return successful result
    return insiderActions({
      createOrUpdateIdmData,
      profile,
      modelLinkCode,
      personalInfo: this.personalInfo,
      leadIds: this.leadIds,
      leadTargets: this.selectedLeadTargets,
      ...additionalInsiderData,
    }).catch(() => true);
  };

  showThankYouPage = () => {
    this.setState({
      formState: LEAD_FORM_STATES.THANK_YOU,
      showSubmitLoadingIndicator: false,
    });
  };

  /**
   * Logs submit error and change form state.
   * @param {object} err
   */
  submitLeadFormError = err => {
    logger.error(`Post lead error - ${err}`);
    this.setState({ formState: LEAD_FORM_STATES.SUBMIT_LEAD_ERROR, showSubmitLoadingIndicator: false });
  };

  /**
   * Resubmits lead from error.
   */
  resubmitLeadForm = () => this.setState({ formState: LEAD_FORM_STATES.LEAD_FORM, showSubmitLoadingIndicator: false });

  /**
   * Reloads thank you page.
   */
  reloadLeadTargets = () => {
    this.setState({ shouldUpdateLeadTargets: true });
  };

  processLeadTargetsAfterSubmit = () => {
    const {
      customConfig: {
        processDataMethods: { processLeadTargetsAfterSubmit },
      },
    } = this.props;

    return Promise.all(
      processLeadTargetsAfterSubmit.map(processLeadTargets => processLeadTargets.bind(this)(this.leadIds))
    );
  };

  /**
   * Toggles selected state of selected vin.
   * @param {string} uid
   * @param {integer} slot
   */
  toggleLeadSelection = (uid, slot) => {
    const selectedLeadIds = { ...this.state.selectedLeadIds };
    if (selectedLeadIds[uid]) {
      delete selectedLeadIds[uid];
    } else {
      selectedLeadIds[uid] = true;
    }

    EventToolbox.fireCustomEvent(
      LEAD_FORM_EVENTS.SELECT_CARD,
      { checked: !!selectedLeadIds[uid], slot: slot + 1 },
      this.leadFormContainer.current
    );

    this.setState({ selectedLeadIds });
  };

  // possible null-value for style - return to default in this case
  updateStyle = style => {
    const { styleId, trim, submodel, submodelIdentifier, updateStyle } = this.props;
    const { leadFormStrategy } = this.state;

    const trimName = get(style, 'trimName', trim);
    const strategy = get(style, 'strategy');

    this.customThankYouData = {
      ...this.customThankYouData,
      trim: trimName,
    };
    this.setState({
      trim: trimName,
      styleId: get(style, 'id', styleId),
      submodel: get(style, 'submodels.name', submodel),
      submodelIdentifier: get(style, 'submodels.slug', submodelIdentifier),
      leadFormStrategy: strategy || leadFormStrategy,
      shouldUpdateLeadTargets: true,
    });

    if (typeof updateStyle === 'function') {
      updateStyle(style, strategy);
    }
  };

  isLeadDeliveryURL = () => get(this.props, 'customConfig.apiParams.leadSubmissionURL') === LEADDELIVERY_API_URL;

  render() {
    const {
      leadTargets,
      formState,
      showListTargetsLoadingIndicator,
      showSubmitLoadingIndicator,
      selectedLeadIds,
      submodel,
      submodelIdentifier,
      styleId,
      trim,
      isCheckboxProcessed,
      isCheckboxProcessError,
    } = this.state;

    const {
      className,
      pageName,
      venomVersion,
      loadingComponent: LoadingComponent,
      leadFormComponent: LeadFormComponent,
      thankYouComponent: ThankYouComponent,
      emptyLeadFormComponent: EmptyLeadFormComponent,
      config,
      validationConfig,
      leadFormData,
      make,
      model,
      year,
      dealerName,
      zipCode,
      stateCode,
      customConfig: { leadFormConfig, thankYouConfig, explainingTextParams, hideDelighted, noLeadTargets, creativeId },
      closeDrawer,
      radius,
      specialIdentifier,
    } = this.props;

    const dataTracking = getCurrentCreativeId(config, this.props.customConfig);

    const errorStateActions = {
      loadDataError: this.reloadLeadTargets,
      submitLeadError: this.resubmitLeadForm,
      loadThankYouError: this.processLeadTargetsAfterSubmit,
    };

    const submittedLeadTargetsData = {
      leadTargets: this.selectedLeadTargets,
      leads: this.selectedLeadTargets,
      leadIds: this.leadIds,
      trackingData: { ...this.submittedTrackingData, radius },
    };

    const isEmptyState = !leadTargets.length && !!EmptyLeadFormComponent;
    return (
      <div
        aria-live="polite"
        ref={this.leadFormContainer}
        className={classNames('lead-form-container', className)}
        data-tracking-parent={dataTracking}
        {...(hideDelighted ? { 'data-hidedelighted': true } : {})}
        data-test={formState === LEAD_FORM_STATES.THANK_YOU ? 'lead-form-thank-you' : 'lead-form'}
      >
        {formState === LEAD_FORM_STATES.LOADING && !!LoadingComponent && <LoadingComponent />}
        {formState === LEAD_FORM_STATES.LEAD_FORM && isEmptyState && (
          <EmptyLeadFormComponent make={make} model={model} year={year} customConfig={leadFormConfig} />
        )}
        {formState === LEAD_FORM_STATES.LEAD_FORM && !isEmptyState && (
          <LeadFormValidationWrapper
            pageName={pageName}
            venomVersion={venomVersion}
            config={config}
            validationConfig={validationConfig}
            leadFormData={leadFormData}
            templateComponent={LeadFormComponent}
            leadTargets={leadTargets}
            selectedLeadIds={selectedLeadIds}
            onToggleLeadSelection={this.toggleLeadSelection}
            onUpdateStyle={this.updateStyle}
            showListTargetsLoadingIndicator={showListTargetsLoadingIndicator}
            showSubmitLoadingIndicator={showSubmitLoadingIndicator}
            submitLeadForm={this.submitLeadForm}
            customConfig={{ ...leadFormConfig, explainingTextParams, noLeadTargets, creativeId }}
            make={make}
            model={model}
            submodel={submodel}
            year={year}
            trim={trim}
            specialIdentifier={specialIdentifier}
            dealerName={dealerName}
            styleId={styleId}
            zipCode={zipCode}
            stateCode={stateCode}
            submittedLeadTargetsData={submittedLeadTargetsData}
            isCheckboxProcessed={isCheckboxProcessed}
            isCheckboxProcessError={isCheckboxProcessError}
          />
        )}
        {formState === LEAD_FORM_STATES.THANK_YOU && (
          <ThankYouComponent
            leadIds={submittedLeadTargetsData.leadIds}
            leadTargets={submittedLeadTargetsData.leadTargets}
            trackingData={submittedLeadTargetsData.trackingData}
            customThankYouData={this.customThankYouData}
            userInfo={this.personalInfo}
            customConfig={{
              ...thankYouConfig,
              explainingTextParams,
              isSimulatedLeadForm: leadFormConfig?.isSimulatedLeadForm,
            }}
            make={make}
            model={model}
            submodel={submodel}
            trim={trim}
            submodelIdentifier={submodelIdentifier}
            year={year}
            styleId={styleId}
            closeDrawer={closeDrawer}
            isCheckboxProcessed={isCheckboxProcessed}
            isCheckboxProcessError={isCheckboxProcessError}
          />
        )}
        {(formState === LEAD_FORM_STATES.LOAD_DATA_ERROR ||
          formState === LEAD_FORM_STATES.SUBMIT_LEAD_ERROR ||
          formState === LEAD_FORM_STATES.LOAD_THANK_YOU_ERROR) && <ErrorState onRetry={errorStateActions[formState]} />}
      </div>
    );
  }
}

export const mapStateToProps = state => ({
  visitorId: state.visitor.id,
  sessionId: state.visitor.session,
  venomVersion: state.venomVersion.version,
  pageName: state.pageContext.page.name,
  profile: get(state, 'profile'),
});

export const stateToPropsConfig = {
  stateCode: bindToPath('location.stateCode', VisitorModel),
  dma: bindToPath('location.dma', VisitorModel),
  latitude: bindToPath('location.latitude', VisitorModel),
  longitude: bindToPath('location.longitude', VisitorModel),
};

const LeadFormWrapperIdm = insiderMethods(LeadFormWrapperUI);
export const LeadFormWrapper = connect(mapStateToProps)(connectToModel(LeadFormWrapperIdm, stateToPropsConfig));
